import { Form } from "@unform/web";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Input, ReactSelectInput } from "../../../components/Input";
import Modal, { ModalTitle, ModalType } from "../../../components/Modal";
import { OrganizationType } from "../../../types/OrganizationTypes";
import api, { catchApiErrorMessage } from "../../../services/api";
import toast from "react-hot-toast";

const downloadPDFReport = async (payload: any, organization: OrganizationType) => {
    try {
        const { data } = await api.get(`/organizations/${organization.id}/report`, {
            params: payload,
            responseType: 'blob',
        })

        // Verificar se a resposta é válida
        if (!data || data.size === 0) {
            throw new Error('Arquivo PDF vazio ou inválido');
        }

        const blob = new Blob([data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `report-${organization.name}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    } catch (error) {
        // Limpar qualquer URL que possa ter sido criado
        console.error('Erro ao baixar relatório:', error);
        throw error; // Re-throw para que o toast.promise possa capturar
    }
}

function GenerateReportModal({ organization, organizationId, ...rest }: ModalType & { organization?: OrganizationType, organizationId?: number }) {
    const { organizationId: urlOrganizationId } = useParams();
    const [fetchedOrganization, setFetchedOrganization] = useState<OrganizationType | null>(null);
    const [loading, setLoading] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);

    useEffect(() => {
        const fetchOrganization = async () => {
            const targetId = organizationId || urlOrganizationId;
            if (targetId && !organization && !hasLoaded) {
                try {
                    setLoading(true);
                    const { data } = await api.get(`/organizations/${targetId}`);
                    setFetchedOrganization(data);
                    setHasLoaded(true);
                } catch (error) {
                    toast.error(catchApiErrorMessage(error));
                } finally {
                    setLoading(false);
                }
            }
        };

        fetchOrganization();
    }, [organizationId, urlOrganizationId, organization, hasLoaded]);

    const currentOrganization = organization || fetchedOrganization;

    // Função para renderizar o formulário com tratamento de erro
    const renderForm = () => {
        try {
            return (
                <Form onSubmit={handleSubmit} onPointerEnterCapture={() => {}} onPointerLeaveCapture={() => {}} placeholder="">
                    <div className="grid grid-flow-col gap-1">
                        <Input
                            type="datetime-local"
                            name="start-date"
                            label="Data inicial"
                        />
                        <Input
                            type="datetime-local"
                            name="end-date"
                            label="Data final"
                        />
                    </div>
                    <ReactSelectInput
                        name="directories"
                        label="Diretórios"
                        isMulti={true}
                        options={currentOrganization?.directories?.map(d => ({ label: d.name, value: d.id })) || []}
                    />
                    <button className="bg-blue-500 hover:bg-blue-600 rounded p-2 text-sm text-white mb-4 mt-2" onClick={handleSubmit}>Gerar relatório</button>
                </Form>
            );
        } catch (error) {
            console.error('Erro ao renderizar formulário:', error);
            return (
                <div className="p-4 text-center text-red-500">
                    <p>Erro ao carregar o formulário. Tente fechar e abrir o modal novamente.</p>
                </div>
            );
        }
    };

    function handleSubmit(payload: any) {
        try {
            if (!currentOrganization) {
                toast.error('Dados da empresa não disponíveis');
                return;
            }

            // Validar se há diretórios selecionados
            if (!payload.directories || payload.directories.length === 0) {
                toast.error('Selecione pelo menos um diretório');
                return;
            }

            const promise = downloadPDFReport(payload, currentOrganization)
            toast.promise(promise, {
                loading: 'Gerando relatório...',
                success: 'Relatório criado com sucesso.',
                error: (err) => {
                    console.error('Erro no relatório:', err);
                    return catchApiErrorMessage(err);
                },
            })
        } catch (error) {
            console.error('Erro inesperado:', error);
            toast.error('Erro inesperado ao gerar relatório');
        }
    }

    if (!currentOrganization && loading) {
        return <Modal {...rest}>
            <ModalTitle title="Gerar relatório" subtitle="Carregando dados da empresa..." />
            <div className="p-4 text-center">
                <p>Carregando...</p>
            </div>
        </Modal>
    }

    if (!currentOrganization) {
        return <Modal {...rest}>
            <ModalTitle title="Gerar relatório" subtitle="Erro ao carregar dados" />
            <div className="p-4 text-center">
                <p>Não foi possível carregar os dados da empresa.</p>
            </div>
        </Modal>
    }

    return <Modal {...rest}>
        <ModalTitle title="Gerar relatório" subtitle={`Empresa: ${currentOrganization.name}`} />
        {renderForm()}
    </Modal>
}

export default GenerateReportModal;